import React, { useCallback, useEffect, useRef } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { Box, Grid, makeStyles, Typography } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { AutocompleteField } from '../../components/form/AutocompleteField';
import { FormField } from '../../components/form/FormField';
import { ScrollableContent } from '../../components/ScrollableContent';
import { SelectedServiceHeader } from '../../components/SelectedServiceHeader';
import { ContactDetailsValues } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';

import { CA_STATES_LIST, getPlaceholdersByCountry, US_STATES_LIST } from './consts';
import { onValidate } from './validation';

const UNITED_STATES = 'United States';
export const CANADA = 'Canada';

const COUNTRY_LIST = [UNITED_STATES, CANADA];

const useStyles = makeStyles((theme: HCPTheme) => ({
  addressSection: {
    paddingBottom: theme.spacing(2),
  },
}));

type FieldName = keyof ContactDetailsValues;

const navigateToFirstFormError = (form: HTMLFormElement) => {
  for (let i = 0; i < form.length; i += 1) {
    const isInputInvalid = form[i]?.ariaInvalid === 'true';

    if (isInputInvalid) {
      form[i]?.scrollIntoView({ behavior: 'smooth' });
      break;
    }
  }
};

const clearStateValue = (values: ContactDetailsValues, mutators: any) => {
  if (!values.state) {
    return;
  }
  // clears the state value if country was changed
  if (values.country && (values.country === CANADA ? US_STATES_LIST : CA_STATES_LIST).includes(values.state)) {
    mutators.clearCountryState();
  }
};

export const ContactDetailsStep = () => {
  const { addressSection } = useStyles();
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const formRef = useRef<HTMLFormElement>(null);

  const formOnChange = useCallback(
    ({ dirty, values, valid }, mutators: any) => {
      if (dirty) {
        clearStateValue(values, mutators);
        dispatch(actions.updateContactDetailsValues(values));
        dispatch(actions.updateContactDetailsValid(valid));
      }
    },
    [actions, dispatch]
  );

  useEffect(() => {
    if (state.contactDetails.submitting && formRef.current) {
      // @see https://final-form.org/docs/react-final-form/faq#via-documentgetelementbyid
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      dispatch(actions.stopSubmittingContactDetailsForm());
    }
  }, [state.contactDetails.submitting, actions, dispatch]);

  useEffect(() => {
    if (state.contactDetails.submitting && formRef.current && !state.contactDetails.valid) {
      setTimeout(() => {
        if (formRef.current) navigateToFirstFormError(formRef.current);
        // Timeout is required, because we need to render errors on screen and then move to it
      }, 100);
    }
  }, [state.contactDetails.submitting, state.contactDetails.valid, actions, dispatch]);

  const handleFormSubmit = () => {
    if (state.contactDetails.valid) {
      dispatch(actions.nextStep());
    }
  };

  return (
    <>
      <SelectedServiceHeader />
      <ScrollableContent>
        <Typography gutterBottom variant="h5">
          Add your contact details
        </Typography>

        <Form<ContactDetailsValues>
          onSubmit={handleFormSubmit}
          initialValues={state.contactDetails.values}
          validate={onValidate}
          mutators={{
            clearCountryState: (_args, formState, utils) => {
              utils.changeValue(formState, 'state', () => '');
            },
          }}
        >
          {({ handleSubmit, values, form }) => (
            <form ref={formRef} onSubmit={handleSubmit} aria-label="Contact details">
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="firstName"
                    data-testid="online-booking-contact-firstname"
                    label="First name"
                    fullWidth
                    autoComplete="given-name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="lastName"
                    data-testid="online-booking-contact-lastname"
                    label="Last name"
                    fullWidth
                    autoComplete="family-name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="phone"
                    data-testid="online-booking-contact-phone"
                    label="Phone number"
                    mask="phone"
                    fullWidth
                    autoComplete="tel-national"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="email"
                    data-testid="online-booking-contact-email"
                    label="E-mail address"
                    type="email"
                    fullWidth
                    autoComplete="email"
                  />
                </Grid>
              </Grid>

              <Box my={2}>
                <Typography variant="body1">
                  Please add the address where you want the service to take place.
                </Typography>
              </Box>

              <Grid container spacing={1} className={addressSection}>
                <Grid item xs={12} md={6}>
                  <AutocompleteField
                    label="Country"
                    name="country"
                    options={COUNTRY_LIST}
                    ListboxProps={{
                      'data-testid': 'online-booking-contact-country-list',
                    }}
                    InputProps={{
                      autoComplete: 'address-level1',
                      'data-testid': 'online-booking-contact-country',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AutocompleteField
                    label={getPlaceholdersByCountry(values.country).state}
                    name="state"
                    options={values.country === CANADA ? CA_STATES_LIST : US_STATES_LIST}
                    ListboxProps={{
                      'data-testid': 'online-booking-contact-state-list',
                    }}
                    InputProps={{
                      autoComplete: 'address-level1',
                      'data-testid': 'online-booking-contact-state',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="addressLine1"
                    data-testid="online-booking-contact-street"
                    label="Street address"
                    fullWidth
                    autoComplete="address-line1"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="addressLine2"
                    data-testid="online-booking-contact-unit"
                    label="Unit / apartment / suite"
                    fullWidth
                    autoComplete="address-line2"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="city"
                    data-testid="online-booking-contact-city"
                    label="City"
                    fullWidth
                    autoComplete="address-level2"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField<FieldName>
                    name="zipCode"
                    data-testid="online-booking-contact-zip"
                    label={getPlaceholdersByCountry(values.country).zipCode}
                    fullWidth
                    autoComplete="postal-code"
                  />
                </Grid>
              </Grid>
              <FormSpy<ContactDetailsValues>
                subscription={{ dirty: true, values: true, valid: true }}
                onChange={(args) => formOnChange(args, form.mutators)}
              />
            </form>
          )}
        </Form>
      </ScrollableContent>
    </>
  );
};
